import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Settings as SettingsIcon, KeyRound, Archive, ShieldCheck, Database, Wrench, AlertTriangle } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { API, authHeaders, apiErr } from './adminApi';

const MODULE_LABELS = {
  ledger: 'Ledger',
  estimates: 'Estimates',
  invoices: 'Invoices',
};
const MODULE_HINTS = {
  ledger: 'Manual income / expense tracking. GunMaster is the source of truth.',
  estimates: 'Website-generated estimates + PDF email. Duplicates GunMaster estimates.',
  invoices: 'Website-generated invoices + PDF email. Duplicates GunMaster invoices.',
};

const AdminSettings = ({ onUnauthorized, settings, onSettingsChange }) => {
  const [saving, setSaving] = useState(false);
  const [reviewUrl, setReviewUrl] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNext, setPwdNext] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setReviewUrl(settings.google_review_url || '');
      setRecoveryEmail(settings.recovery_email || '');
    }
  }, [settings]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/api/admin/site-settings`, patch, { headers: authHeaders() });
      onSettingsChange(data);
      toast.success('Saved');
    } catch (err) {
      if (err?.response?.status === 401) { onUnauthorized?.(); return; }
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally { setSaving(false); }
  };

  const toggleArchive = (moduleKey, archived) => {
    const next = { ...settings.archived_modules, [moduleKey]: archived };
    save({ archived_modules: next });
  };

  const savePassword = async () => {
    if (!pwdCurrent || !pwdNext) { toast.error('Fill both fields'); return; }
    if (pwdNext.length < 8) { toast.error('New password must be 8+ characters'); return; }
    setPwdSaving(true);
    try {
      await axios.post(`${API}/api/admin/change-password`, { current_password: pwdCurrent, new_password: pwdNext }, { headers: authHeaders() });
      toast.success('Password changed');
      setPwdCurrent(''); setPwdNext('');
    } catch (err) {
      toast.error(`Failed: ${apiErr(err)}`);
    } finally { setPwdSaving(false); }
  };

  if (!settings) {
    return <div className="text-gray-400 text-sm">Loading settings…</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-settings">
      {/* Shared admin */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" /> Shared admin account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-gray-400">
            TayLord Arms uses one shared admin login. Change the password below or set a recovery email.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300 text-sm">Current password</Label>
              <Input data-testid="settings-current-password" type="password" value={pwdCurrent} onChange={(e) => setPwdCurrent(e.target.value)} className="bg-slate-800 border-slate-700 text-white" />
            </div>
            <div>
              <Label className="text-gray-300 text-sm">New password (8+ chars)</Label>
              <Input data-testid="settings-new-password" type="password" value={pwdNext} onChange={(e) => setPwdNext(e.target.value)} className="bg-slate-800 border-slate-700 text-white" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button data-testid="settings-save-password" onClick={savePassword} disabled={pwdSaving} className="bg-red-600 hover:bg-red-700 text-white">
              <KeyRound className="w-4 h-4 mr-2" /> {pwdSaving ? 'Saving…' : 'Change password'}
            </Button>
          </div>
          <div>
            <Label className="text-gray-300 text-sm">Recovery email (optional)</Label>
            <div className="flex gap-2">
              <Input data-testid="settings-recovery-email" type="email" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} className="bg-slate-800 border-slate-700 text-white" />
              <Button onClick={() => save({ recovery_email: recoveryEmail || null })} disabled={saving} variant="outline" className="border-slate-700 text-white hover:bg-slate-800">Save</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Google review link */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-amber-400" /> Google review link
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-gray-400">
            Stored once here and reused automatically in every review-request email. Paste the URL from Google Business Profile → "Ask for reviews".
          </p>
          <div className="flex gap-2">
            <Input data-testid="settings-review-url" value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} placeholder="https://g.page/r/…/review" className="bg-slate-800 border-slate-700 text-white font-mono text-sm" />
            <Button onClick={() => save({ google_review_url: reviewUrl })} disabled={saving} variant="outline" className="border-slate-700 text-white hover:bg-slate-800">Save</Button>
          </div>
          {reviewUrl.includes('PLACEHOLDER') && (
            <p className="text-xs text-amber-300">⚠ Placeholder URL — replace before sending real review requests.</p>
          )}
        </CardContent>
      </Card>

      {/* Maintenance Mode */}
      <MaintenanceCard settings={settings} onSave={(patch, msg) => save(patch).then(() => msg && toast.success(msg))} />

      {/* Archived modules */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Archive className="w-5 h-5 text-gray-400" /> Archived modules
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-gray-400">
            These tabs are hidden from the admin nav because GunMaster is the official record. All data is preserved — flip the switch to bring a tab back.
          </p>
          {Object.keys(MODULE_LABELS).map((key) => {
            const archived = settings.archived_modules[key];
            return (
              <div key={key} className="flex items-center justify-between bg-slate-950/40 border border-slate-800 rounded-md p-3">
                <div>
                  <div className="text-white text-sm font-semibold flex items-center gap-2">
                    {MODULE_LABELS[key]}
                    {archived ? (
                      <span className="text-[10px] uppercase tracking-widest bg-gray-800 text-gray-400 border border-gray-700 rounded px-2 py-0.5">Archived</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest bg-emerald-950 text-emerald-300 border border-emerald-800 rounded px-2 py-0.5">Active</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{MODULE_HINTS[key]}</p>
                </div>
                {archived ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`settings-unarchive-${key}`} size="sm" variant="outline" className="border-amber-800 text-amber-300 hover:bg-amber-950">Restore</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-slate-900 border-slate-800">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Restore {MODULE_LABELS[key]}?</AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-400">
                          This will bring the tab back to the admin nav. All previously saved data is still there. This won't affect GunMaster.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`settings-unarchive-confirm-${key}`} onClick={() => toggleArchive(key, false)} className="bg-red-600 hover:bg-red-700 text-white">Restore tab</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : (
                  <Switch data-testid={`settings-archive-${key}`} checked={!archived} onCheckedChange={(v) => toggleArchive(key, !v)} />
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Backup status */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-sky-400" /> Backup &amp; restore
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-300 space-y-2">
          <p>Snapshot before this refactor: <span className="font-mono text-emerald-400">pre-admin-refactor</span></p>
          <p className="text-xs text-gray-500">Automatic checkpoints will run before every Publish action once the Website editor ships in Stage 2.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;

const MaintenanceCard = ({ settings, onSave }) => {
  const [local, setLocal] = useState(settings?.maintenance || { enabled: false, heading: '', message: '', show_phone: true, show_email: true, reopening_date: '' });
  useEffect(() => {
    if (settings?.maintenance) setLocal(settings.maintenance);
  }, [settings?.maintenance]);
  const dirty = JSON.stringify(local) !== JSON.stringify(settings?.maintenance || {});
  const toggle = (patch) => onSave({ maintenance: { ...local, ...patch } });
  const save = () => onSave({ maintenance: local }, 'Maintenance settings saved');

  return (
    <Card className={`bg-slate-900 ${local.enabled ? 'border-amber-800/60' : 'border-slate-800'}`}>
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Wrench className={`w-5 h-5 ${local.enabled ? 'text-amber-400' : 'text-gray-500'}`} /> Site-wide Maintenance Mode
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {local.enabled && (
          <div className="flex items-start gap-2 bg-amber-950/30 border border-amber-800/50 rounded-md p-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-200">Maintenance Mode is <strong>ON</strong>. Public visitors see the placeholder page instead of the site. <strong>/admin</strong> stays open. To preview the real site while maintenance is on, add <code className="font-mono">?preview=1</code> to any page URL.</p>
          </div>
        )}

        <div className="flex items-center justify-between bg-slate-950/40 border border-slate-800 rounded-md p-3">
          <div>
            <div className="text-white text-sm font-semibold">Maintenance Mode</div>
            <div className="text-[11px] text-gray-500">Off = normal site. On = branded &quot;we&apos;ll be right back&quot; page for the public.</div>
          </div>
          <Switch
            data-testid="settings-maintenance-toggle"
            checked={Boolean(local.enabled)}
            onCheckedChange={(v) => { setLocal((c) => ({ ...c, enabled: v })); toggle({ enabled: v }); }}
          />
        </div>

        <div>
          <Label className="text-gray-300 text-xs uppercase tracking-widest">Heading</Label>
          <Input data-testid="settings-maintenance-heading" value={local.heading || ''} onChange={(e) => setLocal((c) => ({ ...c, heading: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
        </div>
        <div>
          <Label className="text-gray-300 text-xs uppercase tracking-widest">Short message</Label>
          <Textarea data-testid="settings-maintenance-message" value={local.message || ''} onChange={(e) => setLocal((c) => ({ ...c, message: e.target.value }))} rows={2} className="bg-slate-800 border-slate-700 text-white" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-gray-300 text-xs uppercase tracking-widest">Reopening date (optional)</Label>
            <Input data-testid="settings-maintenance-reopen" type="date" value={local.reopening_date || ''} onChange={(e) => setLocal((c) => ({ ...c, reopening_date: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" />
          </div>
          <div className="flex items-center justify-between bg-slate-950/40 border border-slate-800 rounded-md px-3">
            <span className="text-gray-300 text-sm">Show phone</span>
            <Switch data-testid="settings-maintenance-show-phone" checked={local.show_phone !== false} onCheckedChange={(v) => setLocal((c) => ({ ...c, show_phone: v }))} />
          </div>
          <div className="flex items-center justify-between bg-slate-950/40 border border-slate-800 rounded-md px-3">
            <span className="text-gray-300 text-sm">Show email</span>
            <Switch data-testid="settings-maintenance-show-email" checked={local.show_email !== false} onCheckedChange={(v) => setLocal((c) => ({ ...c, show_email: v }))} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button data-testid="settings-maintenance-save" onClick={save} disabled={!dirty} className="bg-red-600 hover:bg-red-700 text-white">
            Save maintenance settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
